import { motion } from 'framer-motion'

const GOLD = '#C8923A'
const GOLD_SOFT = '#E0B870'
const INK = 'var(--ink,#ece4d1)'
const SOFT = 'rgba(236,228,209,0.78)'

const SECTION_TITLES: Record<string, string> = {
  INTRODUCAO: 'Introdução',
  'GUIA DE USO': 'Como usar o seu Mapa de Cura dos 4 Elementos',
  DECRETO: 'Decreto de Cocriação Personalizado',
  TERRA: '🌱 Elemento Terra',
  AGUA: '💧 Elemento Água',
  FOGO: '🔥 Elemento Fogo',
  AR: '🌬️ Elemento Ar',
  AFIRMACOES: 'Suas Afirmações do Eu Sou',
  ENCERRAMENTO: 'Encerramento',
}

const ELEMENT_ACCENT: Record<string, string> = {
  TERRA: '#8FAE6F',
  AGUA: '#5FA0D9',
  FOGO: '#E3915A',
  AR: '#C6B2E4',
  AFIRMACOES: GOLD_SOFT,
}

interface Block {
  key: string
  title: string
  body: string
}

/** Compara textos ignorando acentos, emojis, markdown e pontuação. */
function keyOf(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[*_#]/g, '')
    .replace(/[^a-zA-Z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function normalize(text: string): string {
  return text
    .replace(/PADRAO\s+LIMITANTE/gi, 'PADRÃO LIMITANTE')
    .replace(/PADRAO\s+DE\s+USO/gi, 'Guia de uso')
}

/** Divide o texto gerado em blocos por seção (## TITULO). */
export function parseMapa(rawText: string): Block[] {
  const blocks: Block[] = []
  const text = normalize(rawText)
  const lines = text.split('\n')
  let currentKey = 'INTRODUCAO'
  let currentBody: string[] = []

  const push = () => {
    const title = SECTION_TITLES[currentKey] ?? currentKey
    const titleKey = keyOf(title)
    const kept = currentBody.filter(line => {
      const clean = keyOf(line)
      // Remove um título repetido no início do corpo (evita o título duplicado).
      return clean !== titleKey
    })
    const body = kept.join('\n').trim()
    if (body) {
      blocks.push({ key: currentKey, title, body })
    }
  }

  for (const raw of lines) {
    const line = raw.trim()
    const heading = /^#{1,3}\s*(.+)$/.exec(line)
    if (heading) {
      const label = heading[1].replace(/[*_]/g, '').trim()
      const normalized = label.toUpperCase()
      const matched = Object.keys(SECTION_TITLES).find(
        key => normalized === key || normalized.startsWith(key) || normalized.includes(key),
      )
      if (matched) {
        push()
        currentKey = matched
        currentBody = []
        continue
      }
      currentBody.push(`**${label}**`)
      continue
    }
    currentBody.push(raw)
  }
  push()

  if (blocks.length === 0) {
    blocks.push({ key: 'INTRODUCAO', title: 'Seu Mapa de Cura', body: text })
  }
  return blocks
}

/** Renderiza **negrito** e quebras de linha simples. */
function inline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-body" style={{ color: GOLD_SOFT, fontWeight: 600 }}>
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

export default function MapaCuraResult({ text }: { text: string }) {
  const blocks = parseMapa(text)

  return (
    <div className="space-y-10">
      {blocks.map((block, index) => {
        const accent = ELEMENT_ACCENT[block.key] ?? GOLD_SOFT
        const paragraphs = block.body.split(/\n{2,}/).map(p => p.trim()).filter(Boolean)

        return (
          <motion.section
            key={`${block.key}-${index}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: Math.min(index * 0.04, 0.3) }}
            className="card-elevated rounded-lg p-5 sm:p-7"
            style={{ borderColor: `${accent}44` }}
          >
            <h3
              className="font-display type-section mb-5"
              style={{ color: block.key === 'INTRODUCAO' ? INK : accent }}
            >
              {block.title}
            </h3>
            <div className="space-y-4">
              {paragraphs.map((paragraph, i) => {
                const isTriad = /^Eu reconheco|^Eu reconheço/i.test(paragraph)
                return (
                  <p
                    key={i}
                    className="font-body"
                    style={{
                      color: isTriad ? INK : SOFT,
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      borderLeft: isTriad ? `2px solid ${accent}66` : undefined,
                      paddingLeft: isTriad ? '0.9rem' : undefined,
                    }}
                  >
                    {inline(paragraph)}
                  </p>
                )
              })}
            </div>
          </motion.section>
        )
      })}
    </div>
  )
}
